import React, { ComponentProps, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { DrawerBody, DrawerFooter } from '@fiverr-private/overlay';
import { Form, FormControl, FormCounter, FormElement, FormHelperText, Textarea } from '@fiverr-private/forms';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Heading } from '@fiverr-private/typography';
import { SlideFade } from '@fiverr-private/transition';
import { getContext } from '@fiverr-private/fiverr_context';
import { useBriefCreationContext } from '../../context/BriefCreationContext';
import { AppI18n, appTranslate } from '../../i18n';
import { Step } from '../../constants';
import { MAX_INPUT_LENGTH, TEXTAREA_COUNTER_THRESHOLD, useBriefForm } from '../../hooks';
import { briefInputClicked, mixpanelEvents } from '../../monitoring';
import { CustomSlideFade } from '../Animations';
import { usePersonBriefForm } from '../../hooks/usePersonBriefForm';
import { opacityPulseAnimation } from '../Animations/opacityPulseAnimation.ve.css';
import WrapperComponent from '../WrapperComponent';
import { RephraseExamples } from '../RephraseStep/RephraseExamples';
import { useBlurOnLoading } from '../../hooks/useBlurOnLoading';
import { isFromMobileAppByQueryParam } from '../../utils/isFromMobileAppByQueryParam';
import { EducationBanner } from './EducationBanner';
import { GenerateBriefButton } from './GenerateBriefButton';
import { backgroundColorWhite, fullHeight } from './styles.ve.css';

const animationProps = {
  easing: 'linear',
  delay: 'moderate1',
  duration: 'fast3',
} as Partial<Omit<ComponentProps<typeof CustomSlideFade>, 'offsetX' | 'offsetY'>>;

export const CreationStep = () => {
  const { locale } = getContext();
  const { contextId, source, rollouts, parentElement } = useBriefCreationContext();
  const isPersonBriefEnabled = !!rollouts?.inMustangsPersonBrief;

  const personBriefForm = usePersonBriefForm();
  const briefForm = useBriefForm();

  const {
    methods,
    onTextareaChange,
    onSubmit,
    isLoading,
    placeholder,
    handleOnKeyDown,
    handleContentPaste,
    handlePromptKeyDown,
    variant,
  } = isPersonBriefEnabled ? personBriefForm : briefForm;

  useBlurOnLoading(isLoading);

  const VariantWrapper = WrapperComponent(variant);
  const isOnboardingRouting = variant === 'onboarding_routing';

  const EducationBannerComponent = () => (
    <EducationBanner
      currentStep={Step.Creation}
      contextId={contextId}
      source={source || ''}
      variant={variant}
      parentElement={parentElement}
    />
  );

  const validationError = methods.formState.errors.prompt?.message;
  const helperText = useMemo(() => {
    if (isFromMobileAppByQueryParam()) {
      return undefined;
    }

    if (isOnboardingRouting) {
      return appTranslate('creation_step.onboarding.text_area_helper_text');
    }

    if (locale !== 'en-US') {
      return appTranslate('creation_step.english_only');
    }

    return undefined;
  }, [isOnboardingRouting, locale]);

  useEffect(() => {
    mixpanelEvents.seamless(variant).generateBrief.show();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Form methods={methods} onSubmit={onSubmit} height="100%" onKeyDown={handleOnKeyDown}>
      <DrawerBody>
        <CustomSlideFade show offsetX={-20} {...animationProps} onAnimationEnd={() => methods.setFocus('prompt')}>
          <VariantWrapper>
            <Stack direction="column" gap="8" height="100%">
              {!isOnboardingRouting && <EducationBannerComponent />}
              <Stack direction="column" gap="4" height="100%">
                <Heading
                  as={isOnboardingRouting ? 'h3' : 'h6'}
                  size={isOnboardingRouting ? 'h_lg' : 'h_xxs'}
                  fontWeight="bold"
                >
                  <AppI18n k="input_title" />
                </Heading>
                <Container height="100%" className={!isOnboardingRouting ? fullHeight : undefined}>
                  <FormControl name="prompt" maxLength={MAX_INPUT_LENGTH}>
                    <FormElement>
                      <Textarea
                        disabled={isLoading}
                        onClick={() => briefInputClicked(contextId, source || '', undefined, variant)}
                        onChange={onTextareaChange}
                        onPaste={handleContentPaste}
                        onKeyDown={handlePromptKeyDown}
                        placeholder={placeholder}
                        className={classNames(backgroundColorWhite, { [opacityPulseAnimation]: isLoading })}
                        rows={isOnboardingRouting ? 5 : undefined}
                      />
                    </FormElement>

                    {validationError ? (
                      <FormHelperText error={validationError} />
                    ) : (
                      <FormHelperText hint={helperText} />
                    )}

                    {methods.getValues('prompt').length > TEXTAREA_COUNTER_THRESHOLD && <FormCounter />}
                  </FormControl>

                  {isOnboardingRouting && (
                    <Container marginTop="16" display="flex" direction="column" gap="6">
                      <RephraseExamples />
                      <EducationBannerComponent />
                    </Container>
                  )}
                </Container>
              </Stack>
            </Stack>
          </VariantWrapper>
        </CustomSlideFade>
      </DrawerBody>
      <DrawerFooter>
        <SlideFade in offsetY="sm" {...(animationProps as ComponentProps<typeof SlideFade>)}>
          <Container>
            <GenerateBriefButton
              isLoading={isLoading}
              onClick={() => {
                mixpanelEvents.seamless(variant).generateBrief.submit('Click');
                mixpanelEvents.dmBriefCreationDrawerGenerateClick();
              }}
            />
          </Container>
        </SlideFade>
      </DrawerFooter>
    </Form>
  );
};
